import { createAction } from 'redux-actions';
import { isNullOrUndefined } from 'utils/object-utils';
import { LEFT_PANELS } from 'constants/feature/panel-constants';
import { ELEMENT_HIGHLIGHT_STATES } from 'constants/feature/tieout-element-constants';
import { updateElementInCacheAction } from 'store/actions/element-cache-actions';
import { updateElementsSearchByIds } from 'store/actions/statement-navigator/elements-search-panel-actions';
import { updateComfortLettersElementsMapArray } from 'store/actions/common/comfort-annotations-list-actions';
import { getElementInternalReference } from 'store/actions/common/internal-reference-actions';
import {
  getElementDetailsRequest,
  updateElementRequest,
} from 'api/element-api';
import { deleteFormulaRequest } from 'api/formula-api';
import {
  getElementWorkpapers,
  getElementFormulas,
  getElementTickmarks,
  getElementComfortLetters,
} from 'store/actions/element-annotations-actions';
import {
  updateTickmarkElementsMap,
  updateTickmarkElementsMapFromArray,
  fetchTickmarkListForRevisionWithoutLoading,
} from 'store/actions/common/tickmark-list-panel-actions';
import {
  updateWorkpaperElementsMap,
  updateWorkpaperElementsMapFromArray,
} from 'store/actions/common/workpaper-toolkit-list-actions';
import { clearBatchSelectedElementsAction } from 'store/actions/action-creators';
import { getElementHightlightState } from 'constants/feature/statement-content-constants';
import { showElementPanelAction } from 'store/actions/panel-controller-actions';
import { replaceSelectedElementsContentPanel } from 'store/actions/action-creators';
import { fetchStatementSummaryElements } from 'store/actions/statement-summary/elements-summary-actions';

export const elementDetailsLoading = createAction('ELEMENT_DETAILS_LOADING');
export const elementDetailsLoaded = createAction('ELEMENT_DETAILS_LOADED');
export const elementDetailsError = createAction('ELEMENT_DETAILS_ERROR');

export const deleteFormulaError = createAction('DELETE_FORMULA_ERROR');
export const elementFormulaListLoading = createAction(
  'ELEMENT_FORMULA_LIST_LOADING',
);
export const elementFormulaListLoadedWithoutResponse = createAction(
  'ELEMENT_FORMULA_LIST_LOADING_WITHOUT_RESPONSE_ACTION',
);

export const getElementWorkpapersAndUpdateElementCacheWithParameters =
  ({ elementId, sectionId }) =>
  async (dispatch, getState) => {
    Number.isInteger(elementId) &&
      (await dispatch(getElementWorkpapers({ elementId })));

    // Get the updated elementDetails with their new workpapers
    const updatedStoreData = getState().data;
    const updatedElementWPList = updatedStoreData.elementPanel.workpaperList;
    const { elementCache } = updatedStoreData.statementContent;
    const _needToUpdateCacheToo = elementCache.hasElement({
      sectionId,
      elementId,
    });
    if (_needToUpdateCacheToo) {
      const updatedElementForCache = elementCache
        .getElement({
          sectionId,
          elementId,
        })
        .setElementAnnotations({
          workpaperReferenceNumberList: updatedElementWPList.workpapers.map(
            (workpaper) => workpaper.referenceNumber,
          ),
        });
      dispatch(updateElementInCacheAction({ element: updatedElementForCache }));
    }
    dispatch(updateElementsSearchByIds({ elementIds: [elementId.id] }));
  };

export const updateElementDetailsFromLeftPanel =
  ({ elementsArray }) =>
  async (dispatch, getState) => {
    const openedPanel = getState().ui.statementPage.panels.left;
    if (
      openedPanel !== LEFT_PANELS.WORKPAPER &&
      openedPanel !== LEFT_PANELS.TICKMARK &&
      openedPanel !== LEFT_PANELS.COMFORT_ANNOTATION
    ) {
      return;
    }
    //updates workpaper elements map if workpaper left panel is open
    dispatch(
      updateWorkpaperElementsMapFromArray({
        elementsArray,
      }),
    );
    //updates tickmark elements map if tickmark left panel is open
    dispatch(
      updateTickmarkElementsMapFromArray({
        elementsArray,
      }),
    );
    //updates comfort letters elements map if comfort letter left panel is open
    dispatch(updateComfortLettersElementsMapArray({ elementsArray }));
  };

export const fetchSelectedElementDetails =
  ({ elementId }) =>
  async (dispatch, getStore) => {
    const selectedStatement =
      getStore().data &&
      getStore().data.selectedStatement &&
      getStore().data.selectedStatement.data &&
      getStore().data.selectedStatement.data.statement;

    dispatch(elementDetailsLoading());
    await dispatch(getElementDetailsWithoutLoading(elementId));
    dispatch(getElementFormulas({ elementId }));
    dispatch(getElementInternalReference());
    dispatch(getElementWorkpapers({ elementId }));
    dispatch(getElementTickmarks({ elementId }));

    // check if comfort letter feature is enabled before making api call for fetching list of comfort letter
    selectedStatement &&
      selectedStatement.comfortLetter &&
      dispatch(getElementComfortLetters({ elementId }));
  };

export const getElementDetailsWithoutLoading =
  (elementId) => async (dispatch, getStore) => {
    const {
      revision,
      statementContent: { elementCache },
    } = getStore().data;
    try {
      const response = await getElementDetailsRequest({
        elementId,
        revisionId: revision.id,
      });
      if (response && response.data && response.data.result) {
        const elementData = elementCache.getElementsListByElementIds({
          elementIds: [elementId],
        });
        if (elementData.length) {
          const { callout, marker } = elementData[0];
          response.data.result['marker'] = !isNullOrUndefined(marker) && marker;
          response.data.result['callout'] =
            !isNullOrUndefined(callout) && callout;
        }
      }
      dispatch(
        elementDetailsLoaded({
          response,
        }),
      );
    } catch (error) {
      dispatch(elementDetailsError(error));
    }
  };

/** Function used to fetch the currently selected element's attached workpapers
 * and then update the element cache if necessary
 */
export const getElementWorkpapersAndUpdateElementCache =
  () => async (dispatch, getState) => {
    const { elementDetails } = getState().data.elementPanel;
    // fetch the new list of workpapers and update the elementDetails object with them
    await dispatch(getElementWorkpapers({ elementId: elementDetails.id }));

    // Get the updated elementDetails with their new workpapers
    const updatedStoreData = getState().data;
    const updatedElementWPList = updatedStoreData.elementPanel.workpaperList;
    const { elementCache } = updatedStoreData.statementContent;
    const _needToUpdateCacheToo = elementCache.hasElement({
      sectionId: elementDetails.sectionId,
      elementId: elementDetails.id,
    });
    if (_needToUpdateCacheToo) {
      const updatedElementForCache = elementCache
        .getElement({
          sectionId: elementDetails.sectionId,
          elementId: elementDetails.id,
        })
        .setElementAnnotations({
          workpaperReferenceNumberList: updatedElementWPList.workpapers.map(
            (workpaper) => workpaper.referenceNumber,
          ),
        });
      dispatch(updateElementInCacheAction({ element: updatedElementForCache }));
    }
    dispatch(updateElementsSearchByIds({ elementIds: [elementDetails.id] }));
    dispatch(updateElementAnnotationDetails());
  };

export const getElementTickmarksAndUpdateElementCacheWithParameters =
  ({ elementId, revisionId }) =>
  async (dispatch) => {
    dispatch(getElementTickmarks({ elementId: elementId }));
    // will update the element cache and content panel, cache listens for the elementDetailsLoaded action
    dispatch(getElementDetailsWithoutLoading(elementId));
    dispatch(
      fetchTickmarkListForRevisionWithoutLoading({
        revisionId: revisionId,
      }),
    );
    dispatch(updateElementsSearchByIds({ elementIds: [elementId] }));
    dispatch(updateElementAnnotationDetails());
  };

export const updateElementAnnotationDetails =
  () => async (dispatch, getState) => {
    const openedPanel = getState().ui.statementPage.panels.left;
    if (
      openedPanel !== LEFT_PANELS.WORKPAPER &&
      openedPanel !== LEFT_PANELS.TICKMARK
    ) {
      return;
    }

    try {
      const storeData = getState().data;
      const { revision } = storeData;
      const { elementDetails } = storeData.elementPanel;

      const response = await updateElementRequest({
        revisionId: revision.id,
        elementId: elementDetails.id,
        elementDetails,
      });
      dispatch(
        updateWorkpaperElementsMap({
          response,
        }),
      );
      //updates tickmark elements map if tickmark left panel is open
      dispatch(
        updateTickmarkElementsMap({
          response,
        }),
      );
    } catch (error) {
      dispatch(elementDetailsError(error));
    }
  };

export const initElementPanel =
  ({ elementId, color }) =>
  async (dispatch, getStore) => {
    await dispatch(showElementPanelAction());
    await dispatch(fetchSelectedElementDetails({ elementId }));
    const { right } = getStore().ui.statementPage.panels;
    const selectedElements = getStore().ui.statementPage.selectedElementsMap;
    const isElementSelected = elementId in selectedElements;
    const isElementLocated = isElementSelected
      ? selectedElements[elementId].color ===
        ELEMENT_HIGHLIGHT_STATES.PANEL_SELECTED
      : null;
    const { elementDetails } = getStore().data.elementPanel;
    dispatch(
      replaceSelectedElementsContentPanel({
        elementIds: [elementId],
        color: color
          ? color
          : isElementLocated
          ? ELEMENT_HIGHLIGHT_STATES.PANEL_SELECTED
          : getElementHightlightState(right, elementDetails.id, elementId),
      }),
    );
    // clear batch panel if there are any selected elements there
    dispatch(clearBatchSelectedElementsAction());
  };

export const deleteFormulaFromList =
  (formulaId, creationType, type) => async (dispatch, getState) => {
    const storeData = getState().data;
    const { revision } = storeData;
    const { elementDetails } = storeData.elementPanel;
    const socketModel = getState().sockets;
    const { socketHasBeenDisconnected } = socketModel.statementSocket;
    try {
      await deleteFormulaRequest({
        revisionId: revision.id,
        elementId: elementDetails.id,
        formulaId,
        creationType,
        type,
      });

      if (socketHasBeenDisconnected) {
        dispatch(
          replaceSelectedElementsContentPanel({
            elementIds: [elementDetails.id],
          }),
        );
        dispatch(_updateStatementSummaryElements);
        /**
         * We have to fetch the element again as its flaggging display may have changed
         * after a formula is successfully deleted.
         */
        await dispatch(
          fetchSelectedElementDetails({ elementId: elementDetails.id }),
        );
        dispatch(getElementFormulas({ elementId: elementDetails.id }));
        dispatch(updateElementAnnotationDetails());
        dispatch(
          updateElementsSearchByIds({ elementIds: [elementDetails.id] }),
        );
      } else {
        dispatch(elementFormulaListLoading());
        setTimeout(() => {
          dispatch(elementFormulaListLoadedWithoutResponse());
        }, 5000);
      }
    } catch (error) {
      dispatch(deleteFormulaError(error));
    }
  };

export const _updateStatementSummaryElements = async (dispatch, getState) => {
  const { revisionId } = getState().data.elementPanel.elementDetails;
  dispatch(fetchStatementSummaryElements({ revisionId }));
};
